import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { FC, useEffect, useState, Fragment } from 'react';
// Components
import { Add, Close, Search } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Fade,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';
import { Button as SMButton, Loader, Modal, Pagination, Toast } from '../../../components';
import { getPartCategories, getPartPriceRecommended, getParts, getPlaceOrderDuplicate } from '../../../fetch';
import { formatDate, formatMoney, UNDEFINED_PART } from '../../../helpers';
import { IPart, IPartCategory, IPartPrice, IReturnPartPO, IReturnPOPart, ISecondSubPartCategory, ISubPartCategory } from '../../../models';
import { ConfirmationModal } from './confirmation-modal';

interface IFindPartsModal {
  open: boolean;
  onClose: () => void;
  handlePartSelection: (part: any) => void;
  values: any;
  cartParts: any[];
  isReturnOrder?: boolean;
  returnVendorId?: number | string;
  returnParts?: IReturnPartPO[];
  setReturnParts?: (parts: any[]) => void;
}

export const FindPartsModal: FC<IFindPartsModal> = ({ open, onClose, handlePartSelection, values, cartParts, isReturnOrder, returnVendorId, returnParts, setReturnParts }) => {
  const classes = useStyles({ isReturnOrder });
  const [isError, showError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoadingCategories, setLoadingCategories] = useState<boolean>(true);
  const [isLoadingParts, setLoadingParts] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [partCategories, setPartCategories] = useState<IPartCategory[] | null>(null);
  const [subPartCategories, setSubPartCategories] = useState<ISubPartCategory[] | null>(null);
  const [secondSubPartCategories, setSecondSubPartCategories] = useState<ISecondSubPartCategory[] | null>(null);
  const [parts, setParts] = useState<IPart[] | null>(null);
  const [selectedPart, setSelectedPart] = useState<IPart | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<IPartCategory>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<ISubPartCategory | null>(null);
  const [selectedSecondSubCategory, setSelectedSecondSubCategory] = useState<ISecondSubPartCategory | null>(null);
  const [page, setPage] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [isAddingToCart, setAddingToCart] = useState<{
    [val: number]: boolean;
  } | null>(null);

  const isMobile = useMediaQuery('(max-width: 550px)');
  // order type is "Open Ended PO", send a static part category id to get pre-filtered parts
  // others we send nothing or selected id and get back the parts
  const getCategoryId = (catId?: number) => (values.orderType === 20 ? 5999 : catId ?? null);

  const fetchPartCategories = async () => {
    try {
      const res = await getPartCategories({
        partCategoryId: getCategoryId()
      });
      setPartCategories(res);
    } catch (error) {
      console.log(error);
      showError(true);
      setErrorMessage('Error loading categories, please refresh and try again.');
    } finally {
      setLoadingCategories(false);
    }
  };
  const fetchParts = async (searchValue?: string, selectedPage?: number, selectedPerPage?: number, partCategoryId?: number) => {
    setLoadingParts(true);
    try {
      const res = await getParts({
        vendorId: returnVendorId ?? null,
        partCategoryId: getCategoryId(partCategoryId),
        page: (selectedPage || 0) + 1,
        perPage: selectedPerPage || 10,
        // don't search if they apply a category or subcategory
        name: searchValue || undefined
      });
      setParts(res.records);
      setRecordCount(res.totalRecordCount);
    } catch (error) {
      console.log(error);
      showError(true);
      setErrorMessage('Error loading parts, please refresh and try again.');
    } finally {
      setLoadingParts(false);
    }
  };

  useEffect(() => {
    if (!partCategories && open) {
      fetchPartCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFindPartsClose = () => {
    // reset state
    setPage(0);
    setRowsPerPage(10);
    setParts(null);
    setPartCategories(null);
    setSubPartCategories(null);
    setSecondSubPartCategories(null);
    setSelectedCategory(null);
    setSelectedSecondSubCategory(null);
    setSelectedSubCategory(null);
    setSearchValue('');
    setSelectedPart(null);
    onClose();
  };

  const fetchRecommendedVendor = async (part: IPart) => {
    try {
      const res = await getPartPriceRecommended(part.id, {
        neighborhoodId: values.neighborhoodId,
        // lot option is null for selected general job
        lotNumber: values.lotNumber
      });
      if ((res as IPartPrice)?.vendorId) {
        const part: IPartPrice = res as IPartPrice;
        return {
          pricePerUnit: part.price,
          vendorId: part.vendorId
        };
      }
      return {
        pricePerUnit: 0,
        vendorId: 0
      };
    } catch (error) {
      console.log(error);
    }
  };

  // any type here since it can be multiple part types
  const handleAddToCart = async (part: any) => {
    try {
      // set the loading for the button state
      setAddingToCart({
        [part.id]: true
      });
      if (isReturnOrder) {
        // error handling to check the cart for already placed items and not allow them to place the same item with more than allowed
        const previousCartParts = cartParts.filter(p => p.id === part.id);
        const totalPreviousCartParts = previousCartParts.reduce((prev, curr) => prev + Number(curr.quantity), 0) + Number(part.quantity) ?? 0;
        if (previousCartParts.length && totalPreviousCartParts > part.quantityRemaining) {
          showError(true);
          return setErrorMessage('Error adding part to cart, quantity entered is greater than the max allowed.');
        }
        // else callback to set the order with the new part
        return handlePartSelection(part);
      }
      let recommendVendor = await fetchRecommendedVendor(part);
      // set local state which could be used later in the confirmation duplicate modal
      setSelectedPart({
        ...part,
        pricePerUnit: recommendVendor.pricePerUnit ?? 0,
        vendorId: recommendVendor.vendorId ?? 0
      });
      // check the api to see if the part was already ordered recently
      const res = await getPlaceOrderDuplicate({
        neighborhoodId: values.neighborhoodId,
        lotNumber: values.lotNumber,
        partId: part.id
      });
      // if we get a response then show the confirmation modal that they meant to order a duplicate part
      if (res) {
        setShowConfirmationModal(true);
      } else {
        // else callback to set the order with the new part
        handlePartSelection({
          ...part,
          pricePerUnit: recommendVendor.pricePerUnit ?? 0,
          vendorId: recommendVendor.vendorId ?? 0
        });
      }
    } catch (err) {
      console.log(err);
      setSelectedPart(null);
    } finally {
      setAddingToCart(null);
    }
  };
  return (
    <>
      <Modal open={open} onClose={handleFindPartsClose} maxWidth="md">
        <Fade in={open}>
          <div>
            <Grid container spacing={1} direction="column">
              <Grid item>
                <Typography variant="h5">{isReturnOrder ? 'Select Order Parts' : 'Search and Find Parts'}</Typography>
              </Grid>
              {!isReturnOrder && (
                <>
                  <Grid item xs={12} sm={6} className={classes.searchWrapper}>
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder="Search for parts..."
                      name="search"
                      value={searchValue}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            className={classes.searchIcon}
                            onClick={() => {
                              if (searchValue.length > 0) {
                                setSelectedCategory(null);
                                setSelectedSecondSubCategory(null);
                                setSelectedSubCategory(null);
                                setPage(0);
                                setRowsPerPage(10);
                                fetchParts(searchValue, 0, 10);
                              }
                            }}
                          >
                            <Search />
                          </InputAdornment>
                        ),
                        endAdornment: searchValue ? (
                          <InputAdornment
                            position="end"
                            className={classes.searchIcon}
                            onClick={() => {
                              setSearchValue('');
                              setSelectedCategory(null);
                              setSelectedSecondSubCategory(null);
                              setSelectedSubCategory(null);
                            }}
                          >
                            <Close />
                          </InputAdornment>
                        ) : null
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && searchValue.length > 0) {
                          setSelectedCategory(null);
                          setSelectedSecondSubCategory(null);
                          setSelectedSubCategory(null);
                          setPage(0);
                          setRowsPerPage(10);
                          fetchParts(searchValue, 0, 10);
                        }
                      }}
                      onChange={e => {
                        setSearchValue(e.target.value);
                      }}
                    />
                    <div className={classes.searchButtonWrapper}>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={searchValue.length === 0 || isLoadingParts}
                        startIcon={<Search />}
                        className={classes.searchButton}
                        onClick={() => {
                          if (searchValue.length > 0) {
                            setSelectedCategory(null);
                            setSelectedSecondSubCategory(null);
                            setSelectedSubCategory(null);
                            setPage(0);
                            setRowsPerPage(10);
                            fetchParts(searchValue, 0, 10);
                          }
                        }}
                      >
                        Search
                      </Button>
                    </div>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<Add />}
                      onClick={() =>
                        handlePartSelection({
                          id: -(cartParts.length + 1),
                          partName: UNDEFINED_PART,
                          quantity: 1
                        })
                      }
                    >
                      Undefined Part
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography>Find By Category</Typography>
                  </Grid>
                  <Grid container spacing={1} className={classes.categoryWrapper}>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth variant="outlined">
                        <Autocomplete
                          value={selectedCategory ? selectedCategory.className : ''}
                          classes={{
                            paper: classes.paperBorder
                          }}
                          onChange={(event, newValue: IPartCategory) => {
                            if (newValue) {
                              setSelectedCategory(newValue);
                              setSelectedSecondSubCategory(null);
                              setSelectedSubCategory(null);
                              setSearchValue('');
                              fetchParts(undefined, 0, 10, newValue.id);
                              if (newValue && newValue.subCategories && newValue.subCategories.length > 0) {
                                setSubPartCategories(newValue.subCategories);
                              }
                            } else {
                              setSelectedCategory(null);
                            }
                          }}
                          selectOnFocus
                          clearOnBlur
                          handleHomeEndKeys
                          loading={isLoadingCategories}
                          id="category"
                          options={partCategories || []}
                          getOptionLabel={(cat: IPartCategory) => {
                            // Value selected with enter, right from the input
                            if (typeof cat === 'string') {
                              return cat;
                            }
                            return cat.className;
                          }}
                          renderOption={(props, option) => {
                            return (
                              <li {...props} key={option.id}>
                                {option.className}
                              </li>
                            );
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              InputLabelProps={{
                                className: classes.ellipsis
                              }}
                              size="small"
                              autoComplete="off"
                              label="Category"
                              variant="outlined"
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                    {selectedCategory && selectedCategory.subCategories && selectedCategory.subCategories.length > 0 && subPartCategories && subPartCategories.length > 0 && (
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth variant="outlined">
                          <Autocomplete
                            value={selectedSubCategory ? selectedSubCategory.className : ''}
                            classes={{
                              paper: classes.paperBorder
                            }}
                            onChange={(event, newValue: ISubPartCategory) => {
                              if (newValue) {
                                setSelectedSubCategory(newValue);
                                fetchParts(undefined, 0, 10, newValue.id);
                                if (newValue && newValue.subCategories && newValue.subCategories.length > 0) {
                                  setSecondSubPartCategories(newValue.subCategories);
                                }
                              } else {
                                setSelectedSubCategory(null);
                              }
                            }}
                            selectOnFocus
                            clearOnBlur
                            handleHomeEndKeys
                            id="sub-category"
                            options={subPartCategories || []}
                            getOptionLabel={(cat: ISubPartCategory) => {
                              // Value selected with enter, right from the input
                              if (typeof cat === 'string') {
                                return cat;
                              }
                              return cat.className;
                            }}
                            renderInput={params => (
                              <TextField
                                {...params}
                                InputLabelProps={{
                                  className: classes.ellipsis
                                }}
                                size="small"
                                autoComplete="off"
                                label={`${selectedCategory.className} Sub Category`}
                                variant="outlined"
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>
                    )}
                    {selectedSubCategory &&
                      selectedSubCategory.subCategories &&
                      selectedSubCategory.subCategories.length > 0 &&
                      secondSubPartCategories &&
                      secondSubPartCategories.length > 0 && (
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth variant="outlined">
                            <Autocomplete
                              value={selectedSecondSubCategory ? selectedSecondSubCategory.className : ''}
                              classes={{
                                paper: classes.paperBorder
                              }}
                              onChange={(event, newValue: ISecondSubPartCategory) => {
                                if (newValue) {
                                  setSelectedSecondSubCategory(newValue);
                                  fetchParts(undefined, 0, 10, newValue.id);
                                } else {
                                  setSelectedSecondSubCategory(null);
                                }
                              }}
                              selectOnFocus
                              clearOnBlur
                              handleHomeEndKeys
                              id="second-sub-category"
                              options={secondSubPartCategories || []}
                              getOptionLabel={(cat: ISecondSubPartCategory) => {
                                // Value selected with enter, right from the input
                                if (typeof cat === 'string') {
                                  return cat;
                                }
                                return cat.className;
                              }}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  size="small"
                                  InputLabelProps={{
                                    className: classes.ellipsis
                                  }}
                                  autoComplete="off"
                                  label={selectedSubCategory.className}
                                  variant="outlined"
                                />
                              )}
                            />
                          </FormControl>
                        </Grid>
                      )}
                  </Grid>{' '}
                </>
              )}
              <Grid item>
                <Divider />
              </Grid>
              <Grid item>
                <Typography variant="h6">Parts</Typography>
                <Typography variant="caption">
                  {isReturnOrder ? `Enter the quantity you wish to return, can't be more than the amount ordered` : `Enter a quantity and then add to cart.`}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                {isReturnOrder && (
                  <List className={classes.listWrapper}>
                    {returnParts && returnParts.flatMap(part => part.returnPOParts).length === 0 && (
                      <Box height="5rem" display="flex" alignItems="center" flexDirection="column" justifyContent="center">
                        <Typography>No Parts found</Typography>
                      </Box>
                    )}
                    <>
                      {returnParts &&
                        returnParts.map((part: IReturnPartPO, index) => {
                          return (
                            <Fragment key={`${index}`}>
                              <Box height="5rem" display="flex" alignItems="center" flexDirection="column" justifyContent="center">
                                <Typography variant={'h6'}>
                                  Purchase Order: {part.purchaseOrderNumber} Date: {formatDate(part.orderDate)}
                                </Typography>
                              </Box>
                              {part &&
                                part.returnPOParts.map((returnPart: IReturnPOPart, i: number) => {
                                  return (
                                    <ListItem
                                      key={`${index}-${i}`}
                                      classes={{
                                        container: classes.listContainer,
                                        root: classes.listText
                                      }}
                                    >
                                      <Box>
                                        <ListItemText primary={`${returnPart.partName} - ${formatMoney(returnPart.price)}`} />
                                        <ListItemText
                                          key={`list-item-${index}-${i}`}
                                          primary={`Purchase Order: ${part.purchaseOrderNumber} - Qty: ${returnPart.quantityRemaining}`}
                                          className={classes.subText}
                                        />
                                      </Box>
                                      <ListItemSecondaryAction classes={{ root: classes.listAction }}>
                                        <TextField
                                          fullWidth
                                          required
                                          variant="outlined"
                                          autoComplete="nope"
                                          label="Qty"
                                          name="quantity"
                                          size="small"
                                          helperText={`Max value: ${returnPart.quantityRemaining}`}
                                          classes={{
                                            root: classes.textField
                                          }}
                                          InputProps={{
                                            endAdornment: <InputAdornment position="end">{returnPart.unit}</InputAdornment>
                                          }}
                                          value={returnPart.quantity || ''}
                                          onChange={e => {
                                            const re = /^[0-9\b]+$/;
                                            // make sure it is number only before we update state
                                            if (e.target.value === '' || re.test(e.target.value)) {
                                              const copyParts = [...returnParts];
                                              copyParts[index].returnPOParts[i] = {
                                                ...copyParts[index].returnPOParts[i],
                                                quantity: +e.target.value > returnPart.quantityRemaining ? +returnPart.quantityRemaining : +e.target.value,
                                                price: +returnPart.price,
                                                // Copied properties to make return parts map to parts
                                                pricePerUnit: +returnPart.price,
                                                partId: returnPart.id,
                                                quantityRemaining: +returnPart.quantityRemaining,
                                                purchaseOrderNumber: copyParts[index].returnPOParts[i]?.purchaseOrderNumber
                                              };
                                              setReturnParts(copyParts);
                                            }
                                          }}
                                        />
                                        {!isMobile ? (
                                          <SMButton
                                            id="add-to-cart"
                                            type="button"
                                            color="primary"
                                            fullWidth
                                            variant="contained"
                                            loading={isAddingToCart && isAddingToCart[returnPart.id] ? 'Loading...' : ''}
                                            startIcon={<Add />}
                                            disabled={!returnPart.quantity || (isAddingToCart && isAddingToCart[returnPart.id])}
                                            onClick={() => {
                                              handleAddToCart({ ...returnPart, purchaseOrderNumber: +part?.purchaseOrderNumber });
                                            }}
                                          >
                                            Add to Cart
                                          </SMButton>
                                        ) : (
                                          <IconButton
                                            color="primary"
                                            disabled={!returnPart.quantityRemaining || (isAddingToCart && isAddingToCart[returnPart.id])}
                                            title="Select Part"
                                            onClick={() => handleAddToCart({ ...returnPart, purchaseOrderNumber: +part?.purchaseOrderNumber })}
                                            size="large"
                                          >
                                            <Add />
                                          </IconButton>
                                        )}
                                      </ListItemSecondaryAction>
                                    </ListItem>
                                  );
                                })}
                            </Fragment>
                          );
                        })}
                    </>
                  </List>
                )}
                {!isReturnOrder && (
                  <List className={classes.listWrapper}>
                    <>
                      {!isLoadingParts && !parts && <Typography className={classes.loader}>Search or find part by category</Typography>}
                      {isLoadingParts && <Loader position="centered" loaderWrapperClassName={classes.loader} />}
                      {!isLoadingParts && parts && parts.length === 0 && <Typography className={classes.loader}>No parts found</Typography>}
                      {!isLoadingParts &&
                        parts &&
                        parts.map((part: IPart, index) => {
                          return (
                            <ListItem
                              key={`${index}`}
                              classes={{
                                container: classes.listContainer,
                                root: classes.listText
                              }}
                            >
                              <ListItemText primary={part.partName} />
                              <ListItemSecondaryAction classes={{ root: classes.listAction }}>
                                <TextField
                                  fullWidth
                                  required
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="Qty"
                                  name="quantity"
                                  size="small"
                                  classes={{
                                    root: classes.textField
                                  }}
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">{part.unit}</InputAdornment>
                                  }}
                                  value={part.quantity || ''}
                                  onChange={e => {
                                    const re = /^[0-9\b]+$/;
                                    // make sure it is number only before we update state
                                    if (e.target.value === '' || re.test(e.target.value)) {
                                      const copyParts = [...parts];
                                      copyParts[index] = {
                                        ...copyParts[index],
                                        quantity: e.target.value
                                      };
                                      setParts(copyParts);
                                    }
                                  }}
                                />
                                {!isMobile ? (
                                  <SMButton
                                    id="add-to-cart"
                                    type="button"
                                    color="primary"
                                    fullWidth
                                    variant="contained"
                                    loading={isAddingToCart && isAddingToCart[part.id] ? 'Loading...' : ''}
                                    startIcon={<Add />}
                                    disabled={!part.quantity || (isAddingToCart && isAddingToCart[part.id])}
                                    onClick={() => handleAddToCart(part)}
                                  >
                                    Add to Cart
                                  </SMButton>
                                ) : (
                                  <IconButton
                                    color="primary"
                                    disabled={!part.quantity || (isAddingToCart && isAddingToCart[part.id])}
                                    title="Select Part"
                                    onClick={() => handleAddToCart(part)}
                                    size="large"
                                  >
                                    <Add />
                                  </IconButton>
                                )}
                              </ListItemSecondaryAction>
                            </ListItem>
                          );
                        })}
                    </>
                  </List>
                )}

                {!isLoadingParts && parts && parts.length > 0 && !isReturnOrder && (
                  <div className={classes.paginationWrapper}>
                    <Pagination
                      page={page}
                      count={recordCount}
                      rowsPerPage={perPage}
                      setPage={val => {
                        fetchParts(
                          searchValue || undefined,
                          val,
                          perPage,
                          selectedCategory
                            ? selectedCategory.id
                            : selectedSubCategory
                            ? selectedSubCategory.id
                            : selectedSecondSubCategory
                            ? selectedSecondSubCategory.id
                            : undefined
                        );
                        setPage(val);
                      }}
                      setRowsPerPage={val => {
                        fetchParts(
                          searchValue || undefined,
                          0,
                          val,
                          selectedCategory
                            ? selectedCategory.id
                            : selectedSubCategory
                            ? selectedSubCategory.id
                            : selectedSecondSubCategory
                            ? selectedSecondSubCategory.id
                            : undefined
                        );
                        setRowsPerPage(val);
                      }}
                    />
                  </div>
                )}
              </Grid>
            </Grid>
          </div>
        </Fade>
      </Modal>
      <ConfirmationModal
        open={showConfirmationModal}
        handleClose={(val?: string) => {
          if (val && val === 'Yes') {
            handlePartSelection(selectedPart);
          }
          setShowConfirmationModal(false);
        }}
      />
      <Toast id="error" autoHideDuration={5000} message={errorMessage} open={isError} onClose={() => showError(false)} variant="error" />
    </>
  );
};

const useStyles = makeStyles<Theme, { isReturnOrder: boolean }>((theme: Theme) => ({
  searchIcon: {
    cursor: 'pointer',
    color: theme.palette.grey[500]
  },
  paperBorder: {
    border: `1px solid ${theme.palette.grey[300]}`
  },
  cartTitle: {
    marginTop: theme.spacing(1)
  },
  paginationWrapper: {
    margin: theme.spacing(0.5, 0)
  },
  listWrapper: {
    border: `1px solid ${theme.palette.grey[300]}`,
    minHeight: '30rem'
  },
  listContainer: {
    display: 'flex',
    width: '100%',
    paddingTop: 5,
    paddingBottom: 5,
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      justifyContent: 'space-between'
    }
  },
  categoryWrapper: {
    padding: `0 8px 16px 16px`,
    marginTop: 0
  },
  loader: {
    display: 'flex',
    flexDirection: 'column',
    height: '20rem',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center'
  },
  ellipsis: {
    fontSize: 14
  },
  searchWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      width: 'auto'
    }
  },
  listText: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '50%'
    }
  },
  listAction: {
    marginLeft: theme.spacing(1),
    position: `relative`,
    display: 'flex',
    transform: 'none',
    alignItems: ({ isReturnOrder }) => (isReturnOrder ? 'flex-start' : 'center'),
    right: 0,
    top: 0,
    [theme.breakpoints.up('sm')]: {
      width: '48%',
      marginLeft: 'auto',
      marginRight: theme.spacing(1)
    }
  },
  textField: {
    marginRight: theme.spacing(1),
    marginTop: 0,
    marginBottom: 0
  },
  searchButtonWrapper: {
    flex: 1,
    margin: `1rem 0`,
    [theme.breakpoints.up('sm')]: {
      margin: 0
    }
  },
  searchButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      minWidth: '8rem',
      width: 'auto'
    },
    height: 40
  },
  subText: {
    '&& span': {
      fontSize: 14
    }
  }
}));
